import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';
import ErrorFallback from './ErrorFallback';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error?: Error;
	errorInfo?: ErrorInfo;
	previousLocation?: string;
}

class ErrorBoundary extends Component<Props, State> {
	private errorCount = 0;
	private readonly MAX_ERRORS = 3;

	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): Partial<State> {
		return {
			hasError: true,
			error,
			previousLocation: window.location.pathname,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		this.errorCount++;
		logger.error('Error caught by boundary:', error, errorInfo);

		// Prevent infinite error loops
		if (this.errorCount >= this.MAX_ERRORS) {
			logger.error('Maximum error count reached. Preventing further error handling.');
			this.setState({
				error: new Error('Too many errors occurred. Please refresh the page.'),
			});
			return;
		}

		this.setState({
			error,
			errorInfo,
		});

		// You can add error reporting here (e.g., Sentry, LogRocket)
		// reportError(error, errorInfo);
	}

	resetError = () => {
		this.errorCount = 0;
		this.setState({
			hasError: false,
			error: undefined,
			errorInfo: undefined,
		});
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<ErrorFallback
					error={this.state.error}
					resetError={this.resetError}
				/>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
